/**
 * App entry point.
 *
 * Boots the SPA into the `#app` mount point declared in `index.html` and
 * routes between the two screens — lobby (connect form) and channel
 * (in-channel UI) — by tearing down one and constructing the other.
 *
 * The WebSocket URL is derived from the page origin: `${origin}/ws`,
 * with the protocol switched to `wss:` whenever the page itself was
 * loaded over `https:`. In production Caddy reverse-proxies `/ws` on
 * the same origin to the rust server; in dev Vite proxies it the same
 * way (see `vite.config.ts`). The same default works in both.
 *
 * Per-user state is persisted via `localStorage`:
 *  - `nettacco.displayName` — last-used display name
 *
 * The channel secret is **never** persisted; it is entered each session
 * by deliberate design.
 */

import { renderChannel, type ChannelHandle } from "./channel";
import { renderLobby, type LobbyHandle } from "./lobby";
import { HandshakeError, NettaccoTransport } from "./transport";
import { precacheMicPermission } from "./audio/capture";

const STORAGE_DISPLAY_NAME = "nettacco.displayName";

/**
 * WebSocket URL the client opens against the server. Derived from the
 * page origin so the bundle has no baked-in domain — the same `dist/`
 * deploys to every channel.
 */
function defaultWsUrl(): string {
    const scheme = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${scheme}//${window.location.host}/ws`;
}

interface AppState {
    /** The mount point in the page. */
    root: HTMLElement;
    /** Currently rendered screen handle, if any. */
    screen: { kind: "lobby"; handle: LobbyHandle } | { kind: "channel"; handle: ChannelHandle } | null;
    /** Live transport, owned by the app while a session is open. */
    transport: NettaccoTransport | null;
}

const state: AppState = {
    root: document.getElementById("app") ?? raise("missing #app mount point"),
    screen: null,
    transport: null,
};

bootApp();

/** Initialise the app and show the first screen. */
function bootApp(): void {
    showLobby();
}

/** Tear down whichever screen is currently mounted. */
function teardownCurrentScreen(): void {
    if (state.screen !== null) {
        state.screen.handle.destroy();
        state.screen = null;
    }
}

/** Tear down the transport if one is open. */
function teardownTransport(): void {
    if (state.transport !== null) {
        state.transport.disconnect();
        state.transport = null;
    }
}

/** Mount the lobby screen, optionally with an error message from a prior failure. */
function showLobby(errorMessage?: string): void {
    teardownCurrentScreen();
    teardownTransport();

    const lobby = renderLobby({
        container: state.root,
        defaultDisplayName: localStorage.getItem(STORAGE_DISPLAY_NAME) ?? "",
        ...(errorMessage !== undefined ? { initialError: errorMessage } : {}),
        onConnect: async (req) => {
            lobby.setBusy(true);
            lobby.setError(null);

            // Pre-grant the microphone permission while we still have
            // the fresh user gesture from the form submit. iOS Safari
            // requires a recent gesture for getUserMedia, and pre-
            // granting here means first PTT press has no popup
            // interrupting the user mid-sentence. Stream is stopped
            // immediately so OS routing stays in media mode (no
            // Bluetooth HFP flip, no iOS earpiece divert). Failure
            // is silent — the first PTT press will surface any
            // permission issue then.
            await precacheMicPermission();

            const transport = new NettaccoTransport();
            try {
                const helloOk = await transport.connect(
                    defaultWsUrl(),
                    req.secret,
                    req.displayName,
                );
                // Persist the bits that are safe to remember.
                localStorage.setItem(STORAGE_DISPLAY_NAME, req.displayName);

                state.transport = transport;
                showChannel();

                function showChannel(): void {
                    teardownCurrentScreen();
                    const channel = renderChannel({
                        container: state.root,
                        transport,
                        helloOk,
                        onDisconnect: () => showLobby(),
                    });
                    state.screen = { kind: "channel", handle: channel };
                }
            } catch (e) {
                transport.disconnect();
                lobby.setBusy(false);
                lobby.setError(handshakeUserMessage(e));
            }
        },
    });
    state.screen = { kind: "lobby", handle: lobby };
}

/** Map a thrown error from `transport.connect` to a UI-appropriate string. */
function handshakeUserMessage(e: unknown): string {
    if (e instanceof HandshakeError) {
        switch (e.failure.kind) {
            case "rejected":
                switch (e.failure.reason) {
                    case "bad_secret":
                        return "Wrong channel secret.";
                    case "server_full":
                        return "Server is full.";
                    case "protocol_error":
                        return "Protocol error from server.";
                }
                return `Rejected: ${e.failure.reason}`;
            case "timeout":
                return "Server did not respond. Check the URL and try again.";
            case "transport":
                return `Could not reach server: ${e.failure.detail}`;
            case "protocol":
                return `Protocol error: ${e.failure.detail}`;
        }
    }
    if (e instanceof Error) return e.message;
    return "Unknown connection error.";
}

/** Throw with the given message — used to initialise non-nullable state. */
function raise(message: string): never {
    throw new Error(message);
}
